import { useQuery, useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@constants/queryKeys';
import { MINUTE } from '@constants/various';
import useNotification from '@hooks/useNotification';
import { getJobRecommendationById } from '@worker/services/MyJobs/api';

interface UseJobRecommendationByIdOptions {
  enabled?: boolean;
  showErrorToast?: boolean;
}

export function useJobRecommendationById(
  jobRecommendationId: number | null | undefined,
  options?: UseJobRecommendationByIdOptions
) {
  const { showErrorNotification } = useNotification();
  const showErrorToast = options?.showErrorToast ?? true;
  const queryClient = useQueryClient();

  const hasCachedData =
    jobRecommendationId != null &&
    queryClient.getQueryData([
      QueryKeys.MyJobs.JOB_RECOMMENDATION_BY_ID,
      jobRecommendationId,
    ]) != null;

  return useQuery({
    queryKey: [QueryKeys.MyJobs.JOB_RECOMMENDATION_BY_ID, jobRecommendationId],
    queryFn: async () => {
      try {
        return await getJobRecommendationById(jobRecommendationId as number);
      } catch (error) {
        if (showErrorToast) {
          showErrorNotification('Could not load job recommendation.');
        }
        throw error;
      }
    },
    enabled:
      (options?.enabled ?? true) &&
      jobRecommendationId != null &&
      !hasCachedData,
    staleTime: MINUTE,
    retry: false,
  });
}
